import React from 'react';
import { useSelector } from "react-redux";
import {
  Avatar,
  Indicator,
  Group,
  Grid,
  createStyles,
  rem,
  Badge, 
  ActionIcon
} from '@mantine/core';
import { User, Stack2, SquareArrowDown, SquareArrowUp } from 'tabler-icons-react';
import intl from 'react-intl-universal';

const useStyles = createStyles((theme) => ({
  notarback: {
    backgroundColor: `#ffffff`,
    marginBottom: `10px`,
  },
  textsize: {
    fontSize: rem('16px'),
    [theme.fn.smallerThan('lg')]: {
      fontSize: rem('10px'),
    },
  },
}));

type Props = {
  resourceId: string;
  opened: boolean;
  onToggle: () => void;
};

const UserSignDocInfo = ({
  resourceId,
  opened,
  onToggle,
}: Props) => {
  const { classes } = useStyles();

  const meetingUsers = useSelector(
    (state: any) => state.user.meetinguser
  ) || [];

  const userHaveToSignDocs = useSelector(
    (state: any) => state.user.userhavetosigndocs
  ) || [];

  const userHaveToStapelSignDocs = useSelector(
    (state: any) => state.user.userhavetostapelsigndocs
  ) || [];


  


    /* =====================
     Helper
     Wie oft ein Dokument im Stape für einen User vorkommt
     participantId → meetinguser.id
  ===================== */


  const getStapelCountForUser = (
  participantId: string,
  resourceId: string,
  stapelDocs: any[]
) => {
  return (
    (Array.isArray(stapelDocs)
      ? stapelDocs
      : Object.values(stapelDocs)
    ).filter(
      (stapel: any) =>
        stapel.participantId === participantId &&
        stapel.documentsSignModes?.some(
          (d: any) => d.resourceId === resourceId
        )
    ).length
  );
};

  /* =====================
     USERS SINGLE SIGN
     userId → meetinguser.userId
  ===================== */

const singleUsers =
  (Array.isArray(userHaveToSignDocs)
    ? userHaveToSignDocs
    : Object.values(userHaveToSignDocs)
  )
    .filter((d: any) => d.resourceId === resourceId)
    .flatMap((d: any) =>
      d.user.map((userId: string) => {
        const u = meetingUsers.find(
          (m: any) => m.userId === userId
        );
        return u
          ? {
              ...u,
              mode: "single",
              stapelCount: 1,
            }
          : null;
      })
    )
    .filter(Boolean);

  /* =====================
     USERS STAPEL SIGN
     participantId → meetinguser.id
  ===================== */

const stapelUsers =
  (Array.isArray(userHaveToStapelSignDocs)
    ? userHaveToStapelSignDocs
    : Object.values(userHaveToStapelSignDocs)
  )
    .map((stapel: any) => {
      if (
        !stapel.documentsSignModes?.some(
          (d: any) => d.resourceId === resourceId
        )
      ) {
        return null;
      }

      const u = meetingUsers.find(
        (m: any) => m.id === stapel.participantId
      );

      if (!u) return null;

      const stapelCount = getStapelCountForUser(
        stapel.participantId,
        resourceId,
        userHaveToStapelSignDocs
      );

      //console.log("stapelCount:",stapelCount)

      return {
        ...u,
        mode: "stapel",
        stapelCount,
      };
    })
    .filter(Boolean);

  /* =====================
     MERGE + DEDUP
  ===================== */

  const usersToShow = [...singleUsers, ...stapelUsers].filter(
    (u, index, self) =>
      index === self.findIndex(
        (x) =>
          x.id === u.id &&
          x.mode === u.mode
      )
  );

  /* =====================
     RENDER
  ===================== */

  return (
  <>
{usersToShow.length > 0 && (
 <Group position="right">
    <Badge
      color="gray"
      variant="outline"
      pr={3}
      sx={{ cursor: "pointer" }}
      onClick={onToggle}
      rightSection={
        <ActionIcon
          size="xs"
          variant="transparent"
          color="gray"
          sx={{ pointerEvents: "none" }} 
        >
          {opened ? (
            <SquareArrowUp size={14} />
          ) : (
            <SquareArrowDown size={14} />
          )}
        </ActionIcon>
      }
    >
      {intl.get('USERSIGNEDOCINFO_ALLSIGNERS_BUTTON')} ({usersToShow.length})
    </Badge>
  </Group>
)}



    {opened && (
      <>
       

        {usersToShow.map((item: any, index: number) => {
          const initials =
            item.lastName?.[0] + item.firstName?.[0];

          const fullname =
            `${item.lastName} ${item.firstName}`;

          const inmeetingcolor =
            item.meetingParticipantStatus === "InMeeting"
              ? "green"
              : "red";

              
          return (
            <Grid key={item.id || index}>
              <Grid.Col xs={2} xl={1}>
                <Indicator
                  inline
                  size={10}
                  offset={2}
                  position="bottom-end"
                  color={inmeetingcolor}
                  withBorder
                >
                  <Avatar
                    alt={fullname}
                    radius="xl"
                    size="sm"
                  >
                    {initials}
                  </Avatar>
                </Indicator>
              </Grid.Col>

              <Grid.Col xs={7} xl={7}>
                <Group spacing={6}>
                  {item.mode === 'single' && <User size={16} />}
                   {item.mode === "stapel" && (
                      <>
                        <Stack2 size={16} />
                        {item.stapelCount > 1 && (
                          <Badge color="gray" size="xs" radius="xs">{item.stapelCount}</Badge>
                          
                        )}
                      </>
                    )}
                  <span className={classes.textsize}>
                    {fullname}
                  </span>
                </Group>
              </Grid.Col>
            </Grid>
          );
        })}
      </>
    )}
  </>
);
};


export default UserSignDocInfo;
